import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

export default function DataMetricsSection({
  tx,
  setDataRange,
  dataRange,
  fetchDataMetrics,
  buttonStyle,
  dataLoading,
  dataError,
  dataMetrics,
  numberFmt,
  locale,
  chartColors,
  insightsFallbackUsed,
  insightsFallbackReason,
  insightsWindowDays,
  insightsLoading,
  insightsError,
  materialInsights,
  selectedMaterial,
  setSelectedMaterial,
  fetchMaterialDetail,
  materialDetail,
  materialPipelineStatus,
  materialPipelineStatusLoading,
  materialPipelineStatusError,
  fetchMaterialPipelineStatus,
}) {
  const pipelineHealth = materialPipelineStatus?.health || "idle";
  const run = materialPipelineStatus?.run || {};
  const completion = materialPipelineStatus?.enrichment_completion || {};
  const totalMaterials = Number(completion.materials_total || run.materials_total || 0);
  const healthColor =
    pipelineHealth === "running"
      ? "#6ADFA0"
      : pipelineHealth === "stalled"
      ? "#f6c95f"
      : pipelineHealth === "failed"
      ? "#ff8b8b"
      : "#9fb0c7";
  const statusLabel =
    pipelineHealth === "running"
      ? tx("Running", "Läuft", "运行中")
      : pipelineHealth === "stalled"
      ? tx("Stalled", "Gestoppt", "停滞")
      : pipelineHealth === "failed"
      ? tx("Failed", "Fehlgeschlagen", "失败")
      : tx("Idle", "Leerlauf", "空闲");
  const progressRows = [
    { key: "details", label: tx("Details", "Details", "详情"), data: completion.details },
    { key: "assessments", label: tx("Assessments", "Bewertungen", "评估"), data: completion.assessments },
    { key: "availability", label: tx("Availability", "Verfügbarkeit", "可用性"), data: completion.availability },
    { key: "country_shares", label: tx("Country shares", "Länderanteile", "国家份额"), data: completion.country_shares },
    { key: "global_productions", label: tx("Global production", "Globale Produktion", "全球产量"), data: completion.global_productions },
    { key: "need_projections", label: tx("Need projections", "Bedarfsprojektion", "需求预测"), data: completion.need_projections },
  ];
  const formatTs = (value) => {
    if (!value) return "—";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString(locale);
  };

  return (
    <div className="col-12">
      <div className="settings-card glass-card-ui">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: "#7BD5F5", fontSize: 12, letterSpacing: 0.3 }}>{tx("Data", "Daten", "数据")}</div>
            <div style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>{tx("Material coverage", "Materialabdeckung", "材料覆盖")}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {["7d", "30d", "90d"].map((r) => (
              <button
                key={r}
                onClick={() => setDataRange(r)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 10,
                  border: dataRange === r ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.1)",
                  background: dataRange === r ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.04)",
                  color: "#cfe4ff",
                  cursor: "pointer",
                }}
              >
                {r}
              </button>
            ))}
            <button
              type="button"
              onClick={fetchDataMetrics}
              style={{ ...buttonStyle, padding: "8px 12px" }}
              disabled={dataLoading}
            >
              {tx("Refresh", "Aktualisieren", "刷新")}
            </button>
          </div>
        </div>
        {dataLoading && <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 10 }}>{tx("Loading data metrics…", "Datenmetriken werden geladen…", "正在加载数据指标…")}</div>}
        {dataError && <div style={{ color: "#ffb3b3", fontSize: 12, marginTop: 10 }}>{dataError}</div>}
        {dataMetrics && (
          <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              {[
                { label: "Total materials", value: dataMetrics.summary?.materials_total },
                { label: "Total ticks", value: dataMetrics.summary?.ticks_total },
                { label: "Ticks (30d)", value: dataMetrics.summary?.ticks_last_30d },
                { label: "Materials w/ ticks (30d)", value: dataMetrics.summary?.materials_with_ticks_last_30d },
                { label: "Stale >7d", value: dataMetrics.staleness?.stale_gt_7d },
                { label: "Stale >14d", value: dataMetrics.staleness?.stale_gt_14d },
                { label: "Stale >30d", value: dataMetrics.staleness?.stale_gt_30d },
              ].map((m) => (
                <div key={m.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10, color: "#fff" }}>
                  <div style={{ opacity: 0.7, fontSize: 12 }}>{m.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{numberFmt(m.value)}</div>
                </div>
              ))}
            </div>
            <div style={{ height: 260, background: "#0c111a", borderRadius: 12, padding: 10, position: "relative" }}>
              <div style={{ position: "absolute", top: 8, left: 10, color: "#cfe4ff", fontWeight: 700, fontSize: 12 }}>
                {tx("Daily ticks & coverage", "Tägliche Ticks & Abdeckung", "每日报价点与覆盖率")}
              </div>
              {dataMetrics.time_series?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dataMetrics.time_series} margin={{ top: 26, right: 12, left: 0, bottom: 6 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="ts"
                      tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString(locale, { month: "short", day: "numeric" })}
                      minTickGap={16}
                    />
                    <YAxis stroke="rgba(255,255,255,0.4)" />
                    <Tooltip contentStyle={{ background: "#0f1622", border: "1px solid rgba(255,255,255,0.08)" }} />
                    <Line type="monotone" dataKey="ticks" stroke={chartColors[0]} strokeWidth={2} dot={false} name={tx("Ticks", "Ticks", "报价点")} />
                    <Line type="monotone" dataKey="materials" stroke={chartColors[1]} strokeWidth={2} dot={false} name={tx("Materials w/ ticks", "Materialien mit Ticks", "有报价点的材料")} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ position: "absolute", inset: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.35)", fontSize: 12 }}>
                  {tx("No data yet", "Noch keine Daten", "暂无数据")}
                </div>
              )}
            </div>
            {insightsFallbackUsed && (
              <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 6 }}>
                {insightsFallbackReason || `Limited movement in last 30d - showing ${insightsWindowDays}d window.`}
              </div>
            )}
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.02)" }}>
                <div style={{ color: "#fff", fontWeight: 700, marginBottom: 6 }}>{tx("Top gainers", "Top Gewinner", "涨幅前列")} ({insightsWindowDays}d %)</div>
                {insightsLoading && <div style={{ color: "#cfe4ff", fontSize: 12 }}>{tx("Loading…", "Lädt…", "加载中…")}</div>}
                {insightsError && <div style={{ color: "#ffb3b3", fontSize: 12 }}>{insightsError}</div>}
                {(materialInsights?.gainers || []).slice(0, 5).map((g) => (
                  <div key={g.material_id} style={{ display: "flex", justifyContent: "space-between", color: "#cfe4ff", fontSize: 12 }}>
                    <span>{g.code || g.name}</span>
                    <span>{g.pct_change != null ? `${g.pct_change.toFixed(2)}%` : "-"}</span>
                  </div>
                ))}
              </div>
              <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.02)" }}>
                <div style={{ color: "#fff", fontWeight: 700, marginBottom: 6 }}>{tx("Top losers", "Top Verlierer", "跌幅前列")} ({insightsWindowDays}d %)</div>
                {(materialInsights?.losers || []).slice(0, 5).map((g) => (
                  <div key={g.material_id} style={{ display: "flex", justifyContent: "space-between", color: "#cfe4ff", fontSize: 12 }}>
                    <span>{g.code || g.name}</span>
                    <span>{g.pct_change != null ? `${g.pct_change.toFixed(2)}%` : "-"}</span>
                  </div>
                ))}
              </div>
              <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.02)" }}>
                <div style={{ color: "#fff", fontWeight: 700, marginBottom: 6 }}>{tx("Volatility", "Volatilität", "波动率")} ({insightsWindowDays}d stddev %)</div>
                {(materialInsights?.volatility || []).slice(0, 5).map((g) => (
                  <div key={g.material_id} style={{ display: "flex", justifyContent: "space-between", color: "#cfe4ff", fontSize: 12 }}>
                    <span>{g.code || g.name}</span>
                    <span>{g.volatility != null ? g.volatility.toFixed(2) : "-"}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 280px", maxWidth: 340, minWidth: 260, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12 }}>
                <div style={{ color: "#fff", fontWeight: 700, marginBottom: 8 }}>{tx("Materials", "Materialien", "材料")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 520, overflow: "auto" }}>
                  {(dataMetrics.materials || []).map((m) => {
                    const active = selectedMaterial === m.code;
                    return (
                      <button
                        key={m.id || m.code}
                        type="button"
                        onClick={() => {
                          setSelectedMaterial(m.code);
                          fetchMaterialDetail(m.code);
                        }}
                        style={{
                          border: active ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.08)",
                          borderRadius: 10,
                          padding: 10,
                          background: active ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.02)",
                          color: "#fff",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontWeight: 700 }}>{m.name || m.code}</div>
                          <span style={{ fontSize: 12, color: "#cfe4ff" }}>{m.code}</span>
                        </div>
                        <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 4 }}>
                          {numberFmt(m.tick_count)} {tx("ticks", "Ticks", "报价点")} · {tx("Avg close", "Ø Schlusskurs", "平均收盘")} {m.avg_close != null ? numberFmt(m.avg_close, { maximumFractionDigits: 2 }) : "-"}
                        </div>
                        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                          {tx("Last", "Zuletzt", "最近")}: {m.last_ts ? new Date(m.last_ts).toLocaleString(locale) : "-"}
                        </div>
                      </button>
                    );
                  })}
                  {!dataMetrics.materials?.length && <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{tx("No materials.", "Keine Materialien.", "没有材料。")}</div>}
                </div>
              </div>

              <div style={{ flex: "1 1 420px", minWidth: 320, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12 }}>
                {materialDetail ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ color: "#fff", fontWeight: 700 }}>
                        {materialDetail.name || materialDetail.code} - {tx("Detail (30d)", "Details (30d)", "详情（30天）")}
                      </div>
                      {materialDetail.gap_days_30d != null && (
                        <div style={{ color: "#cfe4ff", fontSize: 12 }}>{tx("Gap days", "Lückentage", "缺失天数")}: {materialDetail.gap_days_30d}</div>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 10, marginBottom: 10 }}>
                      {[
                        { label: "Latest close", value: materialDetail.latest_close != null ? numberFmt(materialDetail.latest_close, { maximumFractionDigits: 2 }) : "-" },
                        { label: "Last tick", value: materialDetail.latest_ts ? new Date(materialDetail.latest_ts).toLocaleString() : "-" },
                        { label: "Ticks (30d)", value: numberFmt(materialDetail.tick_count_30d) },
                        { label: tx("Avg close (30d)", "Ø Schlusskurs (30d)", "平均收盘（30天）"), value: materialDetail.avg_close_30d != null ? numberFmt(materialDetail.avg_close_30d, { maximumFractionDigits: 2 }) : "-" },
                        { label: "Min/Max (30d)", value: `${materialDetail.min_close_30d != null ? numberFmt(materialDetail.min_close_30d, { maximumFractionDigits: 2 }) : "-"} / ${materialDetail.max_close_30d != null ? numberFmt(materialDetail.max_close_30d, { maximumFractionDigits: 2 }) : "-"}` },
                        { label: tx("Volatility (30d)", "Volatilität (30d)", "波动率（30天）"), value: materialDetail.stddev_close_30d != null ? numberFmt(materialDetail.stddev_close_30d, { maximumFractionDigits: 2 }) : "-" },
                      ].map((row) => (
                        <div key={row.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                          <div style={{ opacity: 0.7, fontSize: 12 }}>{row.label}</div>
                          <div style={{ fontWeight: 700, color: "#fff" }}>{row.value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ height: 240 }}>
                      {materialDetail.series_30d?.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={materialDetail.series_30d}>
                            <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                            <XAxis
                              dataKey="ts"
                              tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 10 }}
                              tickFormatter={(v) => new Date(v).toLocaleDateString("de-DE", { month: "short", day: "numeric" })}
                              minTickGap={14}
                            />
                            <YAxis stroke="rgba(255,255,255,0.4)" />
                            <Tooltip contentStyle={{ background: "#0f1622", border: "1px solid rgba(255,255,255,0.08)" }} />
                            <Line type="monotone" dataKey="close" stroke={chartColors[0]} strokeWidth={2} dot={false} name={tx("Avg close", "Ø Schlusskurs", "平均收盘")} />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, padding: 10 }}>{tx("No series for this material.", "Keine Zeitreihe für dieses Material.", "该材料暂无序列数据。")}</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{tx("Select a material to view details.", "Wählen Sie ein Material, um Details anzuzeigen.", "请选择材料以查看详情。")}</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.02)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#fff", fontWeight: 700 }}>{tx("Material pipeline status", "Material-Pipeline-Status", "材料流程状态")}</div>
              <div style={{ color: "#cfe4ff", fontSize: 12 }}>
                {tx("Live status for full import + enrichment cycle", "Live-Status für kompletten Import- und Enrichment-Zyklus", "完整导入与增强周期的实时状态")}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ padding: "4px 10px", borderRadius: 999, border: `1px solid ${healthColor}`, color: healthColor, fontSize: 12, fontWeight: 700 }}>
                {statusLabel}
              </span>
              <button type="button" onClick={fetchMaterialPipelineStatus} style={{ ...buttonStyle, padding: "8px 12px" }} disabled={materialPipelineStatusLoading}>
                {tx("Refresh", "Aktualisieren", "刷新")}
              </button>
            </div>
          </div>
          {materialPipelineStatusLoading && <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 10 }}>{tx("Loading pipeline status…", "Pipeline-Status wird geladen…", "正在加载流程状态…")}</div>}
          {materialPipelineStatusError && <div style={{ color: "#ffb3b3", fontSize: 12, marginTop: 10 }}>{materialPipelineStatusError}</div>}
          {materialPipelineStatus && (
            <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                  <div style={{ opacity: 0.7, fontSize: 12 }}>{tx("Phase / Step", "Phase / Schritt", "阶段 / 步骤")}</div>
                  <div style={{ color: "#fff", fontWeight: 700 }}>{run.phase || "—"} / {run.step || "—"}</div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                  <div style={{ opacity: 0.7, fontSize: 12 }}>{tx("Batch progress", "Batch-Fortschritt", "批次进度")}</div>
                  <div style={{ color: "#fff", fontWeight: 700 }}>{Number(run.batch_current || 0)} / {Number(run.batch_total || 0)}</div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                  <div style={{ opacity: 0.7, fontSize: 12 }}>{tx("Overall progress", "Gesamtfortschritt", "总体进度")}</div>
                  <div style={{ color: "#fff", fontWeight: 700 }}>{Number(run.progress_pct || 0).toFixed(1)}%</div>
                </div>
                <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                  <div style={{ opacity: 0.7, fontSize: 12 }}>{tx("Data freshness", "Datenaktualität", "数据新鲜度")}</div>
                  <div style={{ color: "#fff", fontWeight: 700 }}>{formatTs(materialPipelineStatus.freshness?.latest_tick_ts)}</div>
                </div>
              </div>

              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#cfe4ff" }}>
                  <span>{tx("Current run progress", "Fortschritt des aktuellen Runs", "当前运行进度")}</span>
                  <span>{Number(run.progress_pct || 0).toFixed(1)}%</span>
                </div>
                <div style={{ marginTop: 6, height: 10, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, Number(run.progress_pct || 0)))}%`, background: "linear-gradient(90deg, #7BD5F5, #6ADFA0)" }} />
                </div>
                <div style={{ marginTop: 8, color: "#cfe4ff", fontSize: 12 }}>{run.message || "—"}</div>
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                {progressRows.map((row) => {
                  const ready = Number(row.data?.ready || 0);
                  const pct = Math.max(0, Math.min(100, Number(row.data?.pct || 0)));
                  return (
                    <div key={row.key} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#cfe4ff", fontSize: 12 }}>
                        <span>{row.label}</span>
                        <span>{ready} / {totalMaterials} ({pct.toFixed(1)}%)</span>
                      </div>
                      <div style={{ marginTop: 6, height: 8, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: "#9B7BF5" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
